import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play, Pause, MapPin, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_COLORS = {
  avant:   { bg: "#8B5CF6", label: "Avant travaux" },
  pendant: { bg: "#3B82F6", label: "En cours" },
  apres:   { bg: "#10B981", label: "Après travaux" },
};

export default function GaleriePublique() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [catFilter, setCatFilter] = useState("tous");
  const [currentIdx, setCurrentIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const timerRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/galerie/public/${slug}`)
      .then(res => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const filtered = data
    ? (catFilter === "tous" ? data.media : data.media.filter(m => m.categorie === catFilter))
    : [];

  // Auto-advance carousel
  useEffect(() => {
    if (!isPlaying || isHovering || filtered.length <= 1) return;
    const current = filtered[currentIdx];
    if (current?.type === "video") return; // don't auto-advance during video
    timerRef.current = setTimeout(() => {
      setCurrentIdx(i => (i + 1) % filtered.length);
    }, 4000);
    return () => clearTimeout(timerRef.current);
  }, [currentIdx, isPlaying, isHovering, filtered]);

  // Reset index when filter changes
  useEffect(() => { setCurrentIdx(0); }, [catFilter]);

  const goPrev = useCallback(() => {
    clearTimeout(timerRef.current);
    setCurrentIdx(i => (i > 0 ? i - 1 : filtered.length - 1));
  }, [filtered.length]);

  const goNext = useCallback(() => {
    clearTimeout(timerRef.current);
    setCurrentIdx(i => (i < filtered.length - 1 ? i + 1 : 0));
  }, [filtered.length]);

  if (loading) return (
    <div className="min-h-screen bg-[#0F2744] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-[#F97316] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-[#0F2744] flex items-center justify-center text-center px-4">
      <div>
        <Building2 className="w-16 h-16 text-white/20 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Galerie introuvable</h1>
        <p className="text-white/50">Cette galerie n'existe pas ou n'est plus publique.</p>
      </div>
    </div>
  );

  const current = filtered[currentIdx];
  const cats = ["tous", ...["avant","pendant","apres"].filter(c => data.media.some(m => m.categorie === c))];

  return (
    <div className="min-h-screen bg-[#0B1929] text-white">

      {/* Header */}
      <header className="relative z-10 px-4 py-4 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center overflow-hidden shadow-lg">
            <img src="/logo.png" alt="VGI Construct" className="w-full h-full object-contain" />
          </div>
          <div>
            <p className="font-bold text-base tracking-tight uppercase font-['Barlow_Condensed'] text-white">VGI Construct</p>
            <p className="text-white/50 text-xs">Construction & Rénovation</p>
          </div>
        </div>
        <div className="text-right">
          <h1 className="font-bold text-lg uppercase tracking-wide font-['Barlow_Condensed'] text-white truncate max-w-[180px]">{data.chantier.nom}</h1>
          {data.chantier.adresse && (
            <p className="text-white/40 text-xs flex items-center gap-1 justify-end">
              <MapPin className="w-3 h-3" />{data.chantier.adresse}
            </p>
          )}
        </div>
      </header>

      {/* Category Filter */}
      {cats.length > 1 && (
        <div className="flex gap-2 px-4 py-3 overflow-x-auto border-b border-white/10">
          {cats.map(c => {
            const count = c === "tous" ? data.media.length : data.media.filter(m => m.categorie === c).length;
            const label = c === "tous" ? "Tout voir" : CAT_COLORS[c]?.label || c;
            const color = c === "tous" ? "#64748B" : CAT_COLORS[c]?.bg;
            const active = catFilter === c;
            return (
              <button
                key={c}
                onClick={() => setCatFilter(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0 transition-all flex items-center gap-1.5 border ${active ? 'text-white border-transparent' : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'}`}
                style={active ? { background: color, borderColor: color } : {}}
              >
                {label}
                <span className={`text-[10px] px-1 rounded-full ${active ? 'bg-white/25' : 'bg-white/10'}`}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Main Carousel */}
      {filtered.length === 0 ? (
        <div className="flex items-center justify-center h-[60vh] text-white/30">
          <p>Aucun média dans cette catégorie</p>
        </div>
      ) : (
        <div
          className="relative"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {/* Main display */}
          <div className="relative bg-black overflow-hidden" style={{ height: "calc(100vmin - 60px)", maxHeight: "70vh" }}>
            {current.type === "video" ? (
              <video
                ref={videoRef}
                key={current.id}
                src={current.url}
                controls
                autoPlay
                className="w-full h-full object-contain"
                onEnded={goNext}
              />
            ) : (
              <img
                key={current.id}
                src={current.url}
                alt={current.legende || current.filename}
                className="w-full h-full object-contain animate-fadeIn"
              />
            )}

            {/* Category badge */}
            <div className="absolute top-4 left-4">
              <span
                className="px-3 py-1 rounded-full text-xs font-bold text-white shadow-lg"
                style={{ background: CAT_COLORS[current.categorie]?.bg || '#64748B' }}
              >
                {CAT_COLORS[current.categorie]?.label || current.categorie}
              </span>
            </div>

            {/* Arrows */}
            {filtered.length > 1 && (
              <>
                <button
                  onClick={goPrev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/40 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all backdrop-blur-sm"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={goNext}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/40 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all backdrop-blur-sm"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Play/Pause */}
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="absolute bottom-4 right-4 w-8 h-8 bg-black/40 hover:bg-black/70 rounded-full flex items-center justify-center text-white/70 transition-all backdrop-blur-sm"
            >
              {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>
          </div>

          {/* Legende */}
          {current.legende && (
            <div className="px-4 py-2 bg-[#0F1F35] border-t border-white/5">
              <p className="text-white/70 text-sm text-center">{current.legende}</p>
            </div>
          )}

          {/* Dots */}
          {filtered.length > 1 && (
            <div className="flex justify-center gap-1.5 py-3">
              {filtered.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIdx(i)}
                  className={`rounded-full transition-all ${i === currentIdx ? 'w-6 h-2 bg-[#F97316]' : 'w-2 h-2 bg-white/20 hover:bg-white/40'}`}
                />
              ))}
            </div>
          )}

          {/* Thumbnails strip */}
          {filtered.length > 1 && (
            <div className="flex gap-2 px-4 pb-4 overflow-x-auto">
              {filtered.map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => setCurrentIdx(i)}
                  className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${i === currentIdx ? 'border-[#F97316] opacity-100' : 'border-transparent opacity-50 hover:opacity-80'}`}
                >
                  {item.type === "video" ? (
                    <div className="w-full h-full bg-[#1E293B] flex items-center justify-center">
                      <Play className="w-4 h-4 text-white/60" />
                    </div>
                  ) : (
                    <img src={item.url} alt="" className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-white/10 px-4 py-4 flex items-center justify-between">
        <div>
          <p className="text-white/40 text-xs">Galerie VGI Construct</p>
          <p className="text-white/20 text-xs">{data.media.length} média{data.media.length > 1 ? 's' : ''}</p>
        </div>
        <a
          href="https://vgiconstruct.be"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-[#F97316] hover:text-orange-400 transition-colors font-medium"
        >
          vgiconstruct.be →
        </a>
      </footer>
    </div>
  );
}
